import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchPlans, type Plan } from "@/api/meta";
import { Card, Skeleton } from "@/components";
import { useLang } from "@/i18n/LanguageContext";

function price(plan: Plan, t: (nl: string, en: string) => string): string {
  if (plan.price_eur === null) return t("Op maat", "Custom");
  if (plan.price_eur === 0) return t("Gratis", "Free");
  return `€${plan.price_eur}`;
}

function seats(plan: Plan, t: (nl: string, en: string) => string): string {
  if (plan.default_seats === null) return t("Onbeperkt aantal medewerkers", "Unlimited people");
  return t(`Tot ${plan.default_seats} medewerkers`, `Up to ${plan.default_seats} people`);
}

export function PricingPage() {
  const { t, lang } = useLang();
  const plans = useQuery({ queryKey: ["plans"], queryFn: ({ signal }) => fetchPlans(signal) });

  return (
    <section>
      <div className="wrap">
        <div className="section-head">
          <p className="eyebrow">{t("Prijzen", "Pricing")}</p>
          <h2>{t("Eenvoudig per organisatie", "Simple per-organisation pricing")}</h2>
          <p>{t("Bedragen per maand, exclusief btw. Begin gratis met een proefperiode.", "Amounts per month, excluding VAT. Start free with a trial.")}</p>
        </div>

        {plans.isLoading ? (
          <Skeleton lines={4} />
        ) : (
          <div className="cards">
            {plans.data?.map((plan) => (
              <Card key={plan.key}>
                <p className="card-num">{plan.label.toUpperCase()}</p>
                <h3 style={{ fontSize: 28, margin: "4px 0" }}>{price(plan, t)}<span style={{ fontSize: 14, color: "var(--grey-text)" }}>{plan.price_eur && plan.price_eur > 0 ? t(" /maand", " /month") : ""}</span></h3>
                <p style={{ color: "var(--grey-text)", marginBottom: 10 }}>{lang === "en" ? plan.tagline_en : plan.tagline_nl}</p>
                <p style={{ fontSize: 14 }}>{seats(plan, t)}</p>
                <div style={{ marginTop: 14 }}>
                  {plan.key === "enterprise" ? (
                    <Link to="/" className="btn btn-outline">{t("Neem contact op", "Contact us")}</Link>
                  ) : (
                    <Link to="/signup" className="btn btn-primary">{t("Gratis proberen", "Start free trial")}</Link>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        <p style={{ color: "var(--grey-text)", fontSize: 13.5, marginTop: 24 }}>
          {t(
            "Facturatie verloopt op rekening (sales-led). Alle plannen bevatten certificaten, rapportage-export en AVG-data-export.",
            "Billing is invoice-based (sales-led). All plans include certificates, report exports and GDPR data export.",
          )}
        </p>
      </div>
    </section>
  );
}
